// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Inviting somebody to a machine (G3-05; FR-038, 08 §2).
//
// **An invite is a link, and that is the whole design.** `POST /games` opens a game and
// `POST /games/join` seats somebody into one, both since M8; what was missing was any way for the
// second player to learn the game's id. A notification lane would have been the obvious answer and
// is the wrong one: it needs a delivery mechanism, a read state, an expiry and a place to show
// pending ones — four surfaces for a thing every other product on the internet does with a URL.
//
// So an invite is `/play/<machine>?join=<gameId>`, and the delivery is whatever the player already
// uses to talk to their friends. That also makes it work for somebody who is not a friend yet,
// which is what an invite is usually for.
//
// **A row that cannot act must not be offered** — the M8-63 rule. An invite to a game that has
// ended is refused *by name* rather than failing silently, because the two are indistinguishable
// from inside a headset and one of them is a link somebody sent an hour ago.

/** What a caller needs to know about an invite it has just made. */
export interface Invite {
  readonly gameId: string
  /** The address to send. Absolute, because it is going into a message rather than into a router. */
  readonly url: string
}

/**
 * The address that seats somebody into a game.
 *
 * `join` rather than a path segment, because the machine is the subject and the game is a
 * qualifier: a person reading the link should be able to see which machine they are being asked to
 * play, and `/play/neon-nova?join=…` says that where `/games/…` does not.
 */
export const JOIN_PARAM = 'join'

export function inviteUrl(origin: string, machineId: string, gameId: string): string {
  const url = new URL(`/play/${encodeURIComponent(machineId)}`, origin)
  url.searchParams.set(JOIN_PARAM, gameId)
  return url.toString()
}

/**
 * The game id in an address, or nothing.
 *
 * Takes a search string rather than reading `location`, so the play route can be driven through
 * every case without a browser — including the one that matters, which is a link somebody edited.
 *
 * **A uuid or nothing.** A value that is not one was never minted by this product, and handing it
 * to `POST /games/join` would spend a round trip to be told so.
 */
export function joinIdOf(search: string): string | undefined {
  const value = new URLSearchParams(search).get(JOIN_PARAM)
  return value !== null && UUID.test(value) ? value : undefined
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu

/**
 * Put an invite where a player can send it.
 *
 * The clipboard where there is one, and the address bar where there is not — a link the player can
 * see and copy by hand beats a button that reports success and left nothing anywhere. Answers what
 * actually happened so the page can say the right sentence.
 *
 * Takes its clipboard, so a test drives the refusal that a browser without permission produces.
 */
export async function offerInvite(
  invite: Invite,
  clipboard: { writeText: (text: string) => Promise<void> } | undefined = globalThis.navigator
    ?.clipboard,
): Promise<'copied' | 'shown'> {
  if (clipboard === undefined) return 'shown'
  try {
    await clipboard.writeText(invite.url)
    return 'copied'
  } catch {
    // Refused permission, or an insecure origin. The link is still on screen; nothing is lost and
    // there is nothing to recover.
    return 'shown'
  }
}
